import {CommonModule} from '@angular/common';
import {Component, inject, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexGrid,
    ApexLegend,
    ApexStroke,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
    NgApexchartsModule
} from 'ng-apexcharts';
import {catchError, forkJoin, map, Observable, of} from 'rxjs';
import {MaterialModule} from '../../../material.module';
import {AssistantMetrics} from '../../assistants/reports/assistant-metrics.model';
import {ChatbotMetrics} from '../../chatbots/reports/chatbot-metrics.model';
import {TaskMetric} from '../../tasks/reports/task-metrics.model';
import {WorkflowMetric} from '../../workflows/reports/workflow-metric.model';
import {AssistantsService} from '../../../services/objects/assistants.service';
import {ChatbotsService} from '../../../services/objects/chatbots.service';
import {TasksService} from '../../../services/objects/tasks.service';
import {WorkflowsService} from '../../../services/objects/workflows.service';
import {IAssistantOptions} from '../../../models/assistant-options.model';
import {IChatbotOptions} from '../../../models/chatbot-options.model';
import {ITaskOptions} from '../../../models/task-options.model';
import {IWorkflowOptions} from '../../../models/workflow-options.model';
import {
    ShineAiConversationReport,
    ShineAiFunctionCallReport,
    ShineAiReportsService,
    ShineAiReportGrain
} from '../../../services/ai/shine-ai-reports.service';

type SectionId = 'chatbots' | 'assistants' | 'tasks' | 'workflows';
type RangePreset = '7d' | '30d' | '90d' | 'custom';

interface ReportKpi {
    label: string;
    value: string;
    hint: string;
    tone: 'neutral' | 'good' | 'warn' | 'bad';
}

interface ReportColumn {
    key: string;
    label: string;
    align?: 'left' | 'right';
}

interface ReportRow {
    id: string;
    name: string;
    image: string;
    route: string[];
    searchText: string;
    values: Record<string, string | number>;
    healthTone: 'good' | 'warn' | 'bad' | 'neutral';
}

interface ReportSection {
    id: SectionId;
    title: string;
    subtitle: string;
    searchPlaceholder: string;
    filterText: string;
    kpis: ReportKpi[];
    columns: ReportColumn[];
    rows: ReportRow[];
    filteredRows: ReportRow[];
    timelineTitle: string;
    volumeTitle: string;
    timelineSeries: ApexAxisChartSeries;
    volumeSeries: ApexAxisChartSeries;
    chart: ApexChart;
    barChart: ApexChart;
    xAxis: ApexXAxis;
    yAxis: ApexYAxis | ApexYAxis[];
    stroke: ApexStroke;
    dataLabels: ApexDataLabels;
    legend: ApexLegend;
    tooltip: ApexTooltip;
    grid: ApexGrid;
    error?: string;
}

interface EntityWithImage {
    id: string;
    name?: string;
    image?: string;
    widgetOptions?: {
        images?: {
            aiLogo1?: string;
        };
    };
    frontEndOptions?: {
        chatAvatar?: string;
    };
}

interface DailyPoint {
    date: string;
    avgSeconds: number;
    primary: number;
    secondary: number;
    tertiary: number;
}

@Component({
    selector: 'app-overall-dashboard',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MaterialModule,
        NgApexchartsModule,
        RouterLink,
    ],
    templateUrl: './overall-dashboard.component.html',
    styleUrls: ['./overall-dashboard.component.scss']
})
export class OverallDashboardComponent implements OnInit {
    private readonly chatbotsService = inject(ChatbotsService);
    private readonly assistantsService = inject(AssistantsService);
    private readonly tasksService = inject(TasksService);
    private readonly workflowService = inject(WorkflowsService);
    private readonly shineAiReportsService = inject(ShineAiReportsService);

    readonly displayedColumns = ['entity', 'primary', 'secondary', 'tertiary', 'failure', 'avg', 'health'];
    readonly ranges: { label: string; value: RangePreset }[] = [
        {label: '7 days', value: '7d'},
        {label: '30 days', value: '30d'},
        {label: '90 days', value: '90d'},
        {label: 'Custom', value: 'custom'}
    ];
    readonly grains: { label: string; value: ShineAiReportGrain }[] = [
        {label: 'Monthly', value: 'Month'},
        {label: 'Weekly', value: 'Week'},
        {label: 'Daily', value: 'Day'}
    ];

    startDate = '';
    endDate = '';
    selectedRange: RangePreset = '30d';
    selectedGrain: ShineAiReportGrain = 'Week';
    loading = false;
    loadError = '';
    sections: ReportSection[] = [];
    conversationReport: ShineAiConversationReport | null = null;
    conversationKpis: ReportKpi[] = [];
    conversationTrendSeries: ApexAxisChartSeries = [];
    conversationChart: ApexChart = {type: 'area', height: 320, toolbar: {show: false}, zoom: {enabled: false}};
    conversationXAxis: ApexXAxis = {categories: []};
    conversationYAxis: ApexYAxis = {min: 0, labels: {formatter: (value: number) => value.toFixed(0)}};
    conversationStroke: ApexStroke = {curve: 'smooth', width: 3};
    conversationDataLabels: ApexDataLabels = {enabled: false};
    conversationLegend: ApexLegend = {position: 'top', horizontalAlign: 'left'};
    conversationTooltip: ApexTooltip = {y: {formatter: (value: number) => value.toLocaleString()}};
    conversationGrid: ApexGrid = {borderColor: '#e8edf3', strokeDashArray: 4};
    conversationCategorySeries: ApexAxisChartSeries = [];
    conversationCategoryXAxis: ApexXAxis = {categories: []};
    conversationCategoryChart: ApexChart = {type: 'bar', height: 320, toolbar: {show: false}};
    feedbackGaugeStyle = 'conic-gradient(#16855c 0deg, #edf1f5 0deg 180deg)';

    selectedEventChatbot = 'all';
    eventChatbotNames = new Map<string, string>();

    get eventReport(): ShineAiFunctionCallReport | null {
        const report = this.conversationReport?.functionCalls;
        if (!report) return null;
        if (this.selectedEventChatbot === 'all') return report;
        return report.chatbots?.find(bot => this.eventChatbotKey(bot.chatbotId) === this.selectedEventChatbot)?.report ?? null;
    }

    eventChatbotKey(id: string | null): string {
        return id === null ? 'unassigned' : `id:${id}`;
    }

    eventChatbotLabel(id: string | null): string {
        return id === null ? 'Unassigned chatbot' : this.eventChatbotNames.get(id) ?? id;
    }

    eventDimensionLabel(key: string): string {
        const labels: Record<string, string> = {
            lookup: 'Lookups', display: 'Displays', action: 'Actions', other: 'Other types',
            unknown: 'Unknown', succeeded: 'Succeeded', rejected: 'Rejected', failed: 'Failed', cancelled: 'Cancelled'
        };
        return labels[key] ?? 'Unknown';
    }

    ngOnInit(): void {
        this.applyQuickRange('30d');
    }

    applyQuickRange(range: RangePreset): void {
        this.selectedRange = range;
        if (range !== 'custom') {
            const days = range === '7d' ? 7 : range === '90d' ? 90 : 30;
            const end = new Date();
            const start = new Date();
            start.setDate(end.getDate() - (days - 1));
            this.startDate = this.toDateInputValue(start);
            this.endDate = this.toDateInputValue(end);
            this.loadReports();
        }
    }

    applyDateRange(): void {
        if (!this.startDate || !this.endDate) {
            return;
        }
        this.selectedRange = 'custom';
        this.normalizeDateRange();
        this.loadReports();
    }

    applySectionFilter(section: ReportSection, value: string): void {
        const normalized = value.trim().toLowerCase();
        section.filterText = value;
        section.filteredRows = normalized
            ? section.rows.filter(row => row.searchText.includes(normalized))
            : [...section.rows];
    }

    clearSectionFilter(section: ReportSection): void {
        this.applySectionFilter(section, '');
    }

    applyReportGrain(grain: ShineAiReportGrain): void {
        this.selectedGrain = grain;
        this.loadReports();
    }

    trackSection(_: number, section: ReportSection): string {
        return section.id;
    }

    trackRow(_: number, row: ReportRow): string {
        return row.id;
    }

    trackKpi(_: number, kpi: ReportKpi): string {
        return kpi.label;
    }

    trackColumn(_: number, column: ReportColumn): string {
        return column.key;
    }

    private loadReports(): void {
        this.loading = true;
        this.loadError = '';

        forkJoin({
            conversationReport: this.loadConversationReport(),
            sections: forkJoin([
                this.loadChatbotsSection(),
                this.loadAssistantsSection(),
                this.loadTasksSection(),
                this.loadWorkflowsSection()
            ])
        }).subscribe({
            next: ({conversationReport, sections}) => {
                this.setConversationReport(conversationReport);
                this.sections = sections;
                this.loading = false;
                this.loadError = !conversationReport || sections.some(section => section.error)
                    ? 'Some report sources could not be loaded. The available sections are still shown.'
                    : '';
            },
            error: () => {
                this.setConversationReport(null);
                this.sections = [];
                this.loading = false;
                this.loadError = 'Reports could not be loaded. Try again or narrow the date range.';
            }
        });
    }

    private loadConversationReport(): Observable<ShineAiConversationReport | null> {
        return this.shineAiReportsService
            .getConversationSummary(this.startDate, this.endDate, this.selectedGrain)
            .pipe(catchError(() => of(null)));
    }

    private setConversationReport(report: ShineAiConversationReport | null): void {
        this.conversationReport = report;
        if (!report?.functionCalls?.chatbots?.some(bot => this.eventChatbotKey(bot.chatbotId) === this.selectedEventChatbot)) {
            this.selectedEventChatbot = 'all';
        }
        if (!report) {
            this.conversationKpis = [];
            this.conversationTrendSeries = [];
            this.conversationXAxis = {categories: []};
            this.conversationCategorySeries = [];
            this.conversationCategoryXAxis = {categories: []};
            this.feedbackGaugeStyle = 'conic-gradient(#16855c 0deg, #edf1f5 0deg 180deg)';
            return;
        }

        this.conversationKpis = [
            this.kpi('Conversations', report.totalConversations, 'Reportable chatbot conversations', 'neutral'),
            this.kpi('Messages', report.totalMessages, 'Stored user and assistant messages', 'neutral'),
            this.kpi('Conversation minutes', this.formatNumber(report.totalConversationMinutes), 'Total active conversation span', 'neutral'),
            this.kpi('Avg minutes', this.formatNumber(report.averageConversationMinutes), 'Average span per conversation', 'neutral'),
            this.kpi('Avg messages', this.formatNumber(report.averageMessagesPerConversation), 'Messages per conversation', 'neutral'),
            this.kpi('Positive feedback', `${report.positiveFeedbackRate}%`, `${report.thumbsUp} of ${report.feedbackTotal} feedback votes`, this.feedbackTone(report.positiveFeedbackRate, report.feedbackTotal))
        ];

        this.conversationTrendSeries = [
            {name: 'Conversations', data: report.periods.map(period => period.conversations)},
            {name: 'Messages', data: report.periods.map(period => period.messages)},
            {name: 'Minutes', data: report.periods.map(period => Math.round(period.conversationMinutes))}
        ];
        this.conversationXAxis = {
            categories: report.periods.map(period => period.periodLabel),
            labels: {rotate: -35, trim: true}
        };
        this.conversationCategorySeries = [
            {name: 'Conversations', data: (report.categories || []).map(category => category.conversations)}
        ];
        this.conversationCategoryXAxis = {
            categories: (report.categories || []).map(category => category.category),
            labels: {rotate: -35, trim: true}
        };

        const gaugeDegrees = Math.max(0, Math.min(100, report.positiveFeedbackRate)) * 1.8;
        this.feedbackGaugeStyle = `conic-gradient(from 270deg, #16855c 0deg ${gaugeDegrees}deg, #edf1f5 ${gaugeDegrees}deg 180deg, transparent 180deg 360deg)`;
    }

    private loadChatbotsSection(): Observable<ReportSection> {
        return forkJoin({
            metrics: this.chatbotsService.reportAllAsync(this.startDate, this.endDate) as Observable<ChatbotMetrics[]>,
            entities: this.chatbotsService.getAllAsync() as Observable<IChatbotOptions[]>
        }).pipe(
            map(({metrics, entities}) => {
                this.eventChatbotNames = new Map(entities.map(entity => [entity.id, entity.name ?? entity.id]));
                return this.buildChatbotsSection(metrics, entities);
            }),
            catchError(() => {
                this.eventChatbotNames.clear();
                return of(this.emptySection('chatbots', 'Chatbots', 'Widget engagement and answer volume.'));
            })
        );
    }

    private loadAssistantsSection(): Observable<ReportSection> {
        return forkJoin({
            metrics: this.assistantsService.reportAllAsync(this.startDate, this.endDate) as Observable<AssistantMetrics[]>,
            entities: this.assistantsService.getAllAsync() as Observable<IAssistantOptions[]>
        }).pipe(
            map(({metrics, entities}) => this.buildAssistantsSection(metrics, entities)),
            catchError(() => of(this.emptySection('assistants', 'Assistants', 'Routing, response quality, and error pressure.')))
        );
    }

    private loadTasksSection(): Observable<ReportSection> {
        return forkJoin({
            metrics: this.tasksService.reportAllAsync(this.startDate, this.endDate) as Observable<TaskMetric[]>,
            entities: this.tasksService.getAllAsync() as Observable<ITaskOptions[]>
        }).pipe(
            map(({metrics, entities}) => this.buildTasksSection(metrics, entities)),
            catchError(() => of(this.emptySection('tasks', 'Tasks', 'Completion volume, failures, and execution time.')))
        );
    }

    private loadWorkflowsSection(): Observable<ReportSection> {
        return forkJoin({
            metrics: this.workflowService.reportAllAsync(this.startDate, this.endDate) as Observable<WorkflowMetric[]>,
            entities: this.workflowService.getAllAsync() as Observable<IWorkflowOptions[]>
        }).pipe(
            map(({metrics, entities}) => this.buildWorkflowsSection(metrics, entities)),
            catchError(() => of(this.emptySection('workflows', 'Workflows', 'Automation throughput, reliability, and duration.')))
        );
    }

    private buildChatbotsSection(metrics: ChatbotMetrics[], entities: IChatbotOptions[]): ReportSection {
        const rows = this.chatbotRows(metrics, entities);
        const dates = this.dateKeysBetween();
        const daily = this.chatbotDaily(metrics, dates);
        const displayed = this.sum(metrics, metric => metric.totalTimesDisplayed);
        const opened = this.sum(metrics, metric => metric.totalOpenedFromWidget);
        const sent = this.sum(metrics, metric => metric.totalMessageSent);
        const responses = this.sum(metrics, metric => metric.totalResponseReceivedFromAI);
        const blocked = this.sum(metrics, metric => metric.totalMessageBlockedByFilter);
        const avgSeconds = this.weightedAverageSeconds(metrics, metric => metric.avgTimeTakenToRespond, metric => metric.totalResponseReceivedFromAI);

        return this.section({
            id: 'chatbots',
            title: 'Chatbots',
            subtitle: 'Widget engagement and answer volume.',
            searchPlaceholder: 'Filter by chatbot name or id',
            columns: [
                {key: 'primary', label: 'Displayed', align: 'right'},
                {key: 'secondary', label: 'Opened', align: 'right'},
                {key: 'tertiary', label: 'Messages', align: 'right'},
                {key: 'failure', label: 'Blocked', align: 'right'},
                {key: 'avg', label: 'Avg response', align: 'right'},
                {key: 'health', label: 'Open rate', align: 'right'}
            ],
            rows,
            kpis: [
                this.kpi('Displayed', displayed, 'Launcher loads', 'neutral'),
                this.kpi('Opened', opened, 'First modal opens', this.rateTone(opened, displayed)),
                this.kpi('User messages', sent, 'Messages sent into the assistant', 'neutral'),
                this.kpi('AI responses', responses, 'Responses returned by AI', this.rateTone(responses, sent)),
                this.kpi('Blocked', blocked, 'Messages stopped by filters', blocked > 0 ? 'warn' : 'good'),
                this.kpi('Avg response', `${avgSeconds} s`, 'Weighted by AI responses', avgSeconds > 12 ? 'warn' : 'good')
            ],
            timelineTitle: 'Response time',
            volumeTitle: 'Engagement',
            daily,
            volumeNames: ['Displayed', 'Opened', 'Messages']
        });
    }

    private buildAssistantsSection(metrics: AssistantMetrics[], entities: IAssistantOptions[]): ReportSection {
        const rows = this.assistantRows(metrics, entities);
        const dates = this.dateKeysBetween();
        const daily = this.assistantDaily(metrics, dates);
        const routed = this.sum(metrics, metric => metric.totalMessageRoutedToAssistant);
        const responded = this.sum(metrics, metric => metric.totalAssistantResponded);
        const errors = this.sum(metrics, metric => metric.totalErrors);
        const avgSeconds = this.weightedAverageSeconds(metrics, metric => metric.avgTimeTakenToRespond, metric => metric.totalAssistantResponded);

        return this.section({
            id: 'assistants',
            title: 'Assistants',
            subtitle: 'Routing, response quality, and error pressure.',
            searchPlaceholder: 'Filter by assistant name, id, or model',
            columns: [
                {key: 'primary', label: 'Routed', align: 'right'},
                {key: 'secondary', label: 'Responded', align: 'right'},
                {key: 'tertiary', label: 'Model', align: 'left'},
                {key: 'failure', label: 'Errors', align: 'right'},
                {key: 'avg', label: 'Avg response', align: 'right'},
                {key: 'health', label: 'Response rate', align: 'right'}
            ],
            rows,
            kpis: [
                this.kpi('Routed', routed, 'Messages sent to assistants', 'neutral'),
                this.kpi('Responded', responded, 'Assistant responses', this.rateTone(responded, routed)),
                this.kpi('Errors', errors, 'Assistant failures', errors > 0 ? 'bad' : 'good'),
                this.kpi('Response rate', this.percent(responded, routed), 'Responses divided by routed messages', this.rateTone(responded, routed)),
                this.kpi('Avg response', `${avgSeconds} s`, 'Weighted by responses', avgSeconds > 12 ? 'warn' : 'good')
            ],
            timelineTitle: 'Response time',
            volumeTitle: 'Routing',
            daily,
            volumeNames: ['Routed', 'Responded', 'Errors']
        });
    }

    private buildTasksSection(metrics: TaskMetric[], entities: ITaskOptions[]): ReportSection {
        const rows = this.taskRows(metrics, entities);
        const dates = this.dateKeysBetween();
        const daily = this.taskDaily(metrics, dates);
        const called = this.sum(metrics, metric => metric.totalTaskCalled);
        const completed = this.sum(metrics, metric => metric.totalTaskCompleted);
        const failed = this.sum(metrics, metric => metric.totalTaskFailed);
        const avgSeconds = this.weightedAverageSeconds(metrics, metric => metric.avgTaskTimeTaken, metric => metric.totalTaskCompleted);

        return this.section({
            id: 'tasks',
            title: 'Tasks',
            subtitle: 'Completion volume, failures, and execution time.',
            searchPlaceholder: 'Filter by task name, id, call type, or model',
            columns: [
                {key: 'primary', label: 'Called', align: 'right'},
                {key: 'secondary', label: 'Completed', align: 'right'},
                {key: 'tertiary', label: 'Call type', align: 'left'},
                {key: 'failure', label: 'Failed', align: 'right'},
                {key: 'avg', label: 'Avg time', align: 'right'},
                {key: 'health', label: 'Completion rate', align: 'right'}
            ],
            rows,
            kpis: [
                this.kpi('Called', called, 'Task executions requested', 'neutral'),
                this.kpi('Completed', completed, 'Successful task completions', this.rateTone(completed, called)),
                this.kpi('Failed', failed, 'Failed task executions', failed > 0 ? 'bad' : 'good'),
                this.kpi('Completion rate', this.percent(completed, called), 'Completed divided by called', this.rateTone(completed, called)),
                this.kpi('Avg time', `${avgSeconds} s`, 'Weighted by completions', avgSeconds > 30 ? 'warn' : 'good')
            ],
            timelineTitle: 'Execution time',
            volumeTitle: 'Execution volume',
            daily,
            volumeNames: ['Called', 'Completed', 'Failed']
        });
    }

    private buildWorkflowsSection(metrics: WorkflowMetric[], entities: IWorkflowOptions[]): ReportSection {
        const rows = this.workflowRows(metrics, entities);
        const dates = this.dateKeysBetween();
        const daily = this.workflowDaily(metrics, dates);
        const called = this.sum(metrics, metric => metric.totalAutomationCalled);
        const completed = this.sum(metrics, metric => metric.totalAutomationCompleted);
        const failed = this.sum(metrics, metric => metric.totalAutomationFailed);
        const avgSeconds = this.weightedAverageSeconds(
            metrics,
            metric => metric.avgAutomationTimeTaken ?? 0,
            metric => metric.totalAutomationCompleted
        );

        return this.section({
            id: 'workflows',
            title: 'Workflows',
            subtitle: 'Automation throughput, reliability, and duration.',
            searchPlaceholder: 'Filter by workflow name, id, call type, or model',
            columns: [
                {key: 'primary', label: 'Called', align: 'right'},
                {key: 'secondary', label: 'Completed', align: 'right'},
                {key: 'tertiary', label: 'Call type', align: 'left'},
                {key: 'failure', label: 'Failed', align: 'right'},
                {key: 'avg', label: 'Avg time', align: 'right'},
                {key: 'health', label: 'Completion rate', align: 'right'}
            ],
            rows,
            kpis: [
                this.kpi('Called', called, 'Workflow executions requested', 'neutral'),
                this.kpi('Completed', completed, 'Successful workflow runs', this.rateTone(completed, called)),
                this.kpi('Failed', failed, 'Failed workflow runs', failed > 0 ? 'bad' : 'good'),
                this.kpi('Completion rate', this.percent(completed, called), 'Completed divided by called', this.rateTone(completed, called)),
                this.kpi('Avg time', `${avgSeconds} s`, 'Weighted by completions', avgSeconds > 60 ? 'warn' : 'good')
            ],
            timelineTitle: 'Execution time',
            volumeTitle: 'Execution volume',
            daily,
            volumeNames: ['Called', 'Completed', 'Failed']
        });
    }

    private section(input: {
        id: SectionId;
        title: string;
        subtitle: string;
        searchPlaceholder: string;
        columns: ReportColumn[];
        rows: ReportRow[];
        kpis: ReportKpi[];
        timelineTitle: string;
        volumeTitle: string;
        daily: DailyPoint[];
        volumeNames: [string, string, string];
    }): ReportSection {
        const categories = input.daily.map(point => point.date);

        return {
            id: input.id,
            title: input.title,
            subtitle: input.subtitle,
            searchPlaceholder: input.searchPlaceholder,
            filterText: '',
            rows: input.rows,
            filteredRows: [...input.rows],
            columns: input.columns,
            kpis: input.kpis,
            timelineTitle: input.timelineTitle,
            volumeTitle: input.volumeTitle,
            timelineSeries: [{name: 'Average seconds', data: input.daily.map(point => point.avgSeconds)}],
            volumeSeries: [
                {name: input.volumeNames[0], data: input.daily.map(point => point.primary)},
                {name: input.volumeNames[1], data: input.daily.map(point => point.secondary)},
                {name: input.volumeNames[2], data: input.daily.map(point => point.tertiary)}
            ],
            chart: {
                type: 'line',
                height: 300,
                toolbar: {show: false},
                zoom: {enabled: false}
            },
            barChart: {
                type: 'bar',
                height: 300,
                toolbar: {show: false},
                stacked: false
            },
            xAxis: {
                categories,
                labels: {
                    rotate: -45,
                    trim: true
                }
            },
            yAxis: {
                min: 0,
                labels: {
                    formatter: (value: number) => value.toFixed(0)
                }
            },
            stroke: {
                curve: 'smooth',
                width: 3
            },
            dataLabels: {
                enabled: false
            },
            legend: {
                position: 'top',
                horizontalAlign: 'left'
            },
            tooltip: {
                y: {
                    formatter: (value: number) => value.toLocaleString()
                }
            },
            grid: {
                borderColor: '#e8edf3',
                strokeDashArray: 4
            }
        };
    }

    private chatbotRows(metrics: ChatbotMetrics[], entities: IChatbotOptions[]): ReportRow[] {
        const entityMap = this.entityMap(entities);
        const grouped = new Map<string, {
            displayed: number;
            opened: number;
            messages: number;
            blocked: number;
            responseTimeMs: number;
            responseCount: number;
            entity: IChatbotOptions;
        }>();

        metrics.forEach(metric => {
            const entity = entityMap.get(metric.chatbotId);
            if (!entity?.name) {
                return;
            }
            const existing = grouped.get(metric.chatbotId) ?? {
                displayed: 0,
                opened: 0,
                messages: 0,
                blocked: 0,
                responseTimeMs: 0,
                responseCount: 0,
                entity
            };
            existing.displayed += metric.totalTimesDisplayed;
            existing.opened += metric.totalOpenedFromWidget;
            existing.messages += metric.totalMessageSent;
            existing.blocked += metric.totalMessageBlockedByFilter;
            existing.responseTimeMs += metric.avgTimeTakenToRespond * metric.totalResponseReceivedFromAI;
            existing.responseCount += metric.totalResponseReceivedFromAI;
            grouped.set(metric.chatbotId, existing);
        });

        return Array.from(grouped.entries())
            .map(([id, row]) => this.reportRow({
                id,
                entity: row.entity,
                routeBase: 'chatbots',
                primary: row.displayed,
                secondary: row.opened,
                tertiary: row.messages,
                failure: row.blocked,
                avgSeconds: this.seconds(row.responseTimeMs, row.responseCount),
                health: this.percent(row.opened, row.displayed),
                healthTone: this.rateTone(row.opened, row.displayed),
                extraSearch: []
            }))
            .sort((a, b) => Number(b.values['secondary']) - Number(a.values['secondary']));
    }

    private assistantRows(metrics: AssistantMetrics[], entities: IAssistantOptions[]): ReportRow[] {
        const entityMap = this.entityMap(entities);
        const grouped = new Map<string, {
            routed: number;
            responded: number;
            errors: number;
            responseTimeMs: number;
            responseCount: number;
            model: string;
            entity: IAssistantOptions;
        }>();

        metrics.forEach(metric => {
            const entity = entityMap.get(metric.assistantId);
            if (!entity?.name) {
                return;
            }
            const existing = grouped.get(metric.assistantId) ?? {
                routed: 0,
                responded: 0,
                errors: 0,
                responseTimeMs: 0,
                responseCount: 0,
                model: metric.model,
                entity
            };
            existing.routed += metric.totalMessageRoutedToAssistant;
            existing.responded += metric.totalAssistantResponded;
            existing.errors += metric.totalErrors;
            existing.responseTimeMs += metric.avgTimeTakenToRespond * metric.totalAssistantResponded;
            existing.responseCount += metric.totalAssistantResponded;
            existing.model = existing.model || metric.model;
            grouped.set(metric.assistantId, existing);
        });

        return Array.from(grouped.entries())
            .map(([id, row]) => this.reportRow({
                id,
                entity: row.entity,
                routeBase: 'assistants',
                primary: row.routed,
                secondary: row.responded,
                tertiary: row.model || '-',
                failure: row.errors,
                avgSeconds: this.seconds(row.responseTimeMs, row.responseCount),
                health: this.percent(row.responded, row.routed),
                healthTone: this.rateTone(row.responded, row.routed),
                extraSearch: [row.model]
            }))
            .sort((a, b) => Number(b.values['primary']) - Number(a.values['primary']));
    }

    private taskRows(metrics: TaskMetric[], entities: ITaskOptions[]): ReportRow[] {
        const entityMap = this.entityMap(entities);
        const grouped = new Map<string, {
            called: number;
            completed: number;
            failed: number;
            responseTimeMs: number;
            responseCount: number;
            callType: string;
            model: string;
            entity: ITaskOptions;
        }>();

        metrics.forEach(metric => {
            const entity = entityMap.get(metric.taskId);
            if (!entity?.name) {
                return;
            }
            const existing = grouped.get(metric.taskId) ?? {
                called: 0,
                completed: 0,
                failed: 0,
                responseTimeMs: 0,
                responseCount: 0,
                callType: metric.taskCallType,
                model: metric.model,
                entity
            };
            existing.called += metric.totalTaskCalled;
            existing.completed += metric.totalTaskCompleted;
            existing.failed += metric.totalTaskFailed;
            existing.responseTimeMs += metric.avgTaskTimeTaken * metric.totalTaskCompleted;
            existing.responseCount += metric.totalTaskCompleted;
            existing.callType = existing.callType || metric.taskCallType;
            existing.model = existing.model || metric.model;
            grouped.set(metric.taskId, existing);
        });

        return Array.from(grouped.entries())
            .map(([id, row]) => this.reportRow({
                id,
                entity: row.entity,
                routeBase: 'tasks',
                primary: row.called,
                secondary: row.completed,
                tertiary: row.callType || '-',
                failure: row.failed,
                avgSeconds: this.seconds(row.responseTimeMs, row.responseCount),
                health: this.percent(row.completed, row.called),
                healthTone: this.rateTone(row.completed, row.called),
                extraSearch: [row.callType, row.model]
            }))
            .sort((a, b) => Number(b.values['primary']) - Number(a.values['primary']));
    }

    private workflowRows(metrics: WorkflowMetric[], entities: IWorkflowOptions[]): ReportRow[] {
        const entityMap = this.entityMap(entities);
        const grouped = new Map<string, {
            called: number;
            completed: number;
            failed: number;
            responseTimeMs: number;
            responseCount: number;
            callType: string;
            model: string;
            entity: IWorkflowOptions;
        }>();

        metrics.forEach(metric => {
            const entity = entityMap.get(metric.automationId);
            if (!entity?.name) {
                return;
            }
            const existing = grouped.get(metric.automationId) ?? {
                called: 0,
                completed: 0,
                failed: 0,
                responseTimeMs: 0,
                responseCount: 0,
                callType: metric.automationCallType,
                model: metric.model,
                entity
            };
            existing.called += metric.totalAutomationCalled;
            existing.completed += metric.totalAutomationCompleted;
            existing.failed += metric.totalAutomationFailed;
            existing.responseTimeMs += (metric.avgAutomationTimeTaken ?? 0) * metric.totalAutomationCompleted;
            existing.responseCount += metric.avgAutomationTimeTaken === null ? 0 : metric.totalAutomationCompleted;
            existing.callType = existing.callType || metric.automationCallType;
            existing.model = existing.model || metric.model;
            grouped.set(metric.automationId, existing);
        });

        return Array.from(grouped.entries())
            .map(([id, row]) => this.reportRow({
                id,
                entity: row.entity,
                routeBase: 'automations',
                primary: row.called,
                secondary: row.completed,
                tertiary: row.callType || '-',
                failure: row.failed,
                avgSeconds: this.seconds(row.responseTimeMs, row.responseCount),
                health: this.percent(row.completed, row.called),
                healthTone: this.rateTone(row.completed, row.called),
                extraSearch: [row.callType, row.model]
            }))
            .sort((a, b) => Number(b.values['primary']) - Number(a.values['primary']));
    }

    private chatbotDaily(metrics: ChatbotMetrics[], dates: string[]): DailyPoint[] {
        return dates.map(date => {
            const day = metrics.filter(metric => this.dateKey(metric.reportDate) === date);
            return {
                date,
                avgSeconds: this.weightedAverageSeconds(day, metric => metric.avgTimeTakenToRespond, metric => metric.totalResponseReceivedFromAI),
                primary: this.sum(day, metric => metric.totalTimesDisplayed),
                secondary: this.sum(day, metric => metric.totalOpenedFromWidget),
                tertiary: this.sum(day, metric => metric.totalMessageSent)
            };
        });
    }

    private assistantDaily(metrics: AssistantMetrics[], dates: string[]): DailyPoint[] {
        return dates.map(date => {
            const day = metrics.filter(metric => this.dateKey(metric.reportDate) === date);
            return {
                date,
                avgSeconds: this.weightedAverageSeconds(day, metric => metric.avgTimeTakenToRespond, metric => metric.totalAssistantResponded),
                primary: this.sum(day, metric => metric.totalMessageRoutedToAssistant),
                secondary: this.sum(day, metric => metric.totalAssistantResponded),
                tertiary: this.sum(day, metric => metric.totalErrors)
            };
        });
    }

    private taskDaily(metrics: TaskMetric[], dates: string[]): DailyPoint[] {
        return dates.map(date => {
            const day = metrics.filter(metric => this.dateKey(metric.reportDate) === date);
            return {
                date,
                avgSeconds: this.weightedAverageSeconds(day, metric => metric.avgTaskTimeTaken, metric => metric.totalTaskCompleted),
                primary: this.sum(day, metric => metric.totalTaskCalled),
                secondary: this.sum(day, metric => metric.totalTaskCompleted),
                tertiary: this.sum(day, metric => metric.totalTaskFailed)
            };
        });
    }

    private workflowDaily(metrics: WorkflowMetric[], dates: string[]): DailyPoint[] {
        return dates.map(date => {
            const day = metrics.filter(metric => this.dateKey(metric.reportDate) === date);
            return {
                date,
                avgSeconds: this.weightedAverageSeconds(day, metric => metric.avgAutomationTimeTaken ?? 0, metric => metric.totalAutomationCompleted),
                primary: this.sum(day, metric => metric.totalAutomationCalled),
                secondary: this.sum(day, metric => metric.totalAutomationCompleted),
                tertiary: this.sum(day, metric => metric.totalAutomationFailed)
            };
        });
    }

    private reportRow(input: {
        id: string;
        entity: EntityWithImage;
        routeBase: string;
        primary: number;
        secondary: number;
        tertiary: string | number;
        failure: number;
        avgSeconds: number;
        health: string;
        healthTone: 'good' | 'warn' | 'bad' | 'neutral';
        extraSearch: string[];
    }): ReportRow {
        const name = input.entity.name ?? input.id;
        const values: Record<string, string | number> = {
            primary: input.primary,
            secondary: input.secondary,
            tertiary: input.tertiary,
            failure: input.failure,
            avg: `${input.avgSeconds} s`,
            health: input.health
        };

        return {
            id: input.id,
            name,
            image: this.getValidImage(input.entity),
            route: ['/', input.routeBase, input.id],
            values,
            healthTone: input.healthTone,
            searchText: [name, input.id, ...input.extraSearch].join(' ').toLowerCase()
        };
    }

    private emptySection(id: SectionId, title: string, subtitle: string): ReportSection {
        const section = this.section({
            id,
            title,
            subtitle,
            searchPlaceholder: `Filter ${title.toLowerCase()}`,
            columns: [
                {key: 'primary', label: 'Volume', align: 'right'},
                {key: 'secondary', label: 'Completed', align: 'right'},
                {key: 'tertiary', label: 'Type', align: 'left'},
                {key: 'failure', label: 'Failures', align: 'right'},
                {key: 'avg', label: 'Avg time', align: 'right'},
                {key: 'health', label: 'Health', align: 'right'}
            ],
            rows: [],
            kpis: [
                this.kpi('No data', 0, 'This section did not return report data', 'warn')
            ],
            timelineTitle: 'Trend',
            volumeTitle: 'Volume',
            daily: this.dateKeysBetween().map(date => ({date, avgSeconds: 0, primary: 0, secondary: 0, tertiary: 0})),
            volumeNames: ['Volume', 'Completed', 'Failures']
        });
        section.error = 'This report source could not be loaded.';
        return section;
    }

    private entityMap<T extends EntityWithImage>(entities: T[]): Map<string, T> {
        return new Map(entities.map(entity => [entity.id, entity]));
    }

    private dateKeysBetween(): string[] {
        const start = this.parseDateInput(this.startDate);
        const end = this.parseDateInput(this.endDate);
        const dates: string[] = [];
        const cursor = new Date(start);

        while (cursor <= end) {
            dates.push(this.toDateInputValue(cursor));
            cursor.setDate(cursor.getDate() + 1);
        }

        return dates;
    }

    private parseDateInput(value: string): Date {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    private normalizeDateRange(): void {
        if (this.startDate > this.endDate) {
            const tmp = this.startDate;
            this.startDate = this.endDate;
            this.endDate = tmp;
        }
    }

    private toDateInputValue(date: Date): string {
        const offsetMs = date.getTimezoneOffset() * 60_000;
        return new Date(date.getTime() - offsetMs).toISOString().slice(0, 10);
    }

    private dateKey(reportDate: string): string {
        return reportDate.split('T')[0];
    }

    private sum<T>(items: T[], selector: (item: T) => number): number {
        return items.reduce((total, item) => total + selector(item), 0);
    }

    private weightedAverageSeconds<T>(
        items: T[],
        valueSelector: (item: T) => number,
        weightSelector: (item: T) => number
    ): number {
        const totals = items.reduce((acc, item) => {
            const weight = weightSelector(item);
            acc.sum += valueSelector(item) * weight;
            acc.weight += weight;
            return acc;
        }, {sum: 0, weight: 0});

        return this.seconds(totals.sum, totals.weight);
    }

    private seconds(totalMs: number, count: number): number {
        return count > 0 ? Math.round(totalMs / count / 1000) : 0;
    }

    private percent(numerator: number, denominator: number): string {
        if (denominator <= 0) {
            return '0%';
        }
        return `${Math.round((numerator / denominator) * 100)}%`;
    }

    private rateTone(numerator: number, denominator: number): 'good' | 'warn' | 'bad' | 'neutral' {
        if (denominator <= 0) {
            return 'neutral';
        }
        const rate = numerator / denominator;
        if (rate >= 0.9) {
            return 'good';
        }
        if (rate >= 0.7) {
            return 'warn';
        }
        return 'bad';
    }

    private kpi(
        label: string,
        value: number | string,
        hint: string,
        tone: 'neutral' | 'good' | 'warn' | 'bad'
    ): ReportKpi {
        return {
            label,
            value: typeof value === 'number' ? value.toLocaleString() : value,
            hint,
            tone
        };
    }

    private formatNumber(value: number): string {
        return value.toLocaleString(undefined, {
            maximumFractionDigits: 1
        });
    }

    private feedbackTone(
        positiveFeedbackRate: number,
        feedbackTotal: number
    ): 'neutral' | 'good' | 'warn' | 'bad' {
        if (feedbackTotal <= 0) {
            return 'neutral';
        }
        if (positiveFeedbackRate >= 85) {
            return 'good';
        }
        if (positiveFeedbackRate >= 65) {
            return 'warn';
        }
        return 'bad';
    }

    private getValidImage(element?: EntityWithImage): string {
        return (
            element?.image?.trim() ||
            element?.widgetOptions?.images?.aiLogo1?.trim() ||
            element?.frontEndOptions?.chatAvatar?.trim() ||
            'https://www.markedspartner.no/hubfs/Logomark-Color%20(1).png'
        );
    }
}
